import React from 'react';
import type { OnboardingStep } from '../types/ux-remediation';

export const SAMPLE_CASE_DATA: Record<string, unknown> = {
  name: 'State v. Rodriguez',
  practiceArea: 'Criminal Defense',
  clientSide: 'Defense',
  description:
    'Client pulled over for lane drifting. Breathalyzer read .09, but the officer had not calibrated the device in 14 months.',
  theory: 'The breathalyzer evidence is unreliable due to lack of calibration. The manufacturer requires calibration every 6 months. The officer violated protocol by using an uncalibrated device.',
  venue: 'Urban working-class',
  panelSize: 12,
  argument:
    'The officer administered a breathalyzer that had not been calibrated in 14 months. The manufacturer requires calibration every 6 months. The evidence must be suppressed.',
  sampleResults: {
    verdictDistribution: { guilty: 0.4, notGuilty: 0.6 },
    resonanceScore: 0.72,
    swingPersona: 'Juror 7 - Pragmatic Engineer',
    persuasionFactor: 'Emotional appeal to family values combined with technical evidence about calibration failure',
    debriefSummary:
      "The argument was strongest when it challenged the breathalyzer's reliability. It was weakest when it focused on the traffic stop without video evidence.",
  },
};

// ─── Visual helpers ──────────────────────────────────────────────────────────

const ToolCard: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <div className="flex items-start gap-3 p-3 bg-white border border-paper-300 rounded-none">
    <span className="mt-1.5 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
    <div>
      <div className="font-semibold text-sm text-navy-900">{title}</div>
      <div className="text-xs text-paper-700 mt-0.5 leading-relaxed">{desc}</div>
    </div>
  </div>
);

const MockNavHighlight: React.FC = () => (
  <div className="bg-paper-50 border border-paper-300 p-3 mb-4 rounded-none">
    <div className="flex flex-wrap items-center gap-3 text-xs">
      <span className="font-semibold text-navy-900">Sentantia</span>
      <span className="text-paper-500">Dashboard</span>
      <span className="text-paper-500">Cases</span>
      <span className="text-white bg-navy-900 px-2 py-1">AI Tools</span>
      <span className="text-paper-500">Jury Simulation</span>
    </div>
  </div>
);

const CategoryPill: React.FC<{ label: string }> = ({ label }) => (
  <span className="px-2 py-1 border border-paper-300 bg-white text-navy-900 text-xs font-medium rounded-none">
    {label}
  </span>
);

// ─── Steps ───────────────────────────────────────────────────────────────────

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Sentantia',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Test your arguments before trial. Run a jury simulation, see how virtual jurors respond, and refine your strategy with data-backed insight. Every firm gets the first simulation free, no card required.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
            <div className="text-2xl font-bold text-navy-900">25+</div>
            <div className="text-xs text-paper-500 mt-1">AI Tools</div>
          </div>
          <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
            <div className="text-2xl font-bold text-navy-900">12</div>
            <div className="text-xs text-paper-500 mt-1">Juror Personas</div>
          </div>
          <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
            <div className="text-2xl font-bold text-navy-900">6/8/12</div>
            <div className="text-xs text-paper-500 mt-1">Panel Sizes</div>
          </div>
          <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
            <div className="text-2xl font-bold text-navy-900">1</div>
            <div className="text-xs text-paper-500 mt-1">Click Report</div>
          </div>
        </div>
        <div className="bg-white border border-paper-300 p-3 rounded-none">
          <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-2">What Sentantia Does</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-paper-700">
              <span className="w-5 h-5 border border-paper-300 flex items-center justify-center text-navy-900 text-xs font-semibold rounded-none">1</span>
              Build your case and arguments
            </div>
            <div className="flex items-center gap-2 text-sm text-paper-700">
              <span className="w-5 h-5 border border-paper-300 flex items-center justify-center text-navy-900 text-xs font-semibold rounded-none">2</span>
              Run AI jury simulations
            </div>
            <div className="flex items-center gap-2 text-sm text-paper-700">
              <span className="w-5 h-5 border border-paper-300 flex items-center justify-center text-navy-900 text-xs font-semibold rounded-none">3</span>
              Get decision & resonance insights
            </div>
            <div className="flex items-center gap-2 text-sm text-paper-700">
              <span className="w-5 h-5 border border-paper-300 flex items-center justify-center text-navy-900 text-xs font-semibold rounded-none">4</span>
              Refine and export reports
            </div>
          </div>
        </div>
      </div>
    ),
    cta: 'Take the Tour',
    secondaryCta: 'Skip to Dashboard',
  },
  {
    id: 'ai-tools-nav',
    title: 'AI Tools: One Click Away',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Every AI tool is accessible from the <strong>AI Tools</strong> dropdown in the top navigation. No hunting through menus. Just click and pick.
        </p>
        <MockNavHighlight />
        <div className="flex flex-wrap gap-2">
          <CategoryPill label="Argument" />
          <CategoryPill label="Jury" />
          <CategoryPill label="Evidence" />
          <CategoryPill label="Witness" />
          <CategoryPill label="Presentation" />
          <CategoryPill label="Case Strategy" />
        </div>
        <p className="text-xs text-paper-500">
          25+ tools across 6 categories. Each one is designed for a specific trial-prep task.
        </p>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'ai-tools-argument',
    title: 'Build & Test Your Arguments',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-paper-700 leading-relaxed">
          Start with your case facts and let AI generate, test, and compare arguments before you commit to a strategy.
        </p>
        <div className="grid gap-2">
          <ToolCard
            title="Argument Generator"
            desc="Input case facts and get a structured legal argument tailored to your theory."
          />
          <ToolCard
            title="Scenario Builder"
            desc="Simulate alternative case scenarios: what if the witness flips, or new evidence surfaces?"
          />
          <ToolCard
            title="Weakness Detector"
            desc="AI scans your argument for logical gaps, missing evidence, and vulnerable points."
          />
          <ToolCard
            title="Argument Compare"
            desc="Pit two versions of your argument against each other and see which scores stronger."
          />
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'ai-tools-evidence',
    title: 'Analyze Evidence & Prep Witnesses',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-paper-700 leading-relaxed">
          Turn raw evidence into persuasive exhibits and prepare witnesses who can survive cross-examination.
        </p>
        <div className="grid gap-2">
          <ToolCard
            title="Evidence Analyzer"
            desc="Upload documents and get AI analysis of relevance, admissibility, and strategic value."
          />
          <ToolCard
            title="Exhibit Impact Predictor"
            desc="Score how persuasive each exhibit will be with different jury demographics."
          />
          <ToolCard
            title="Witness Prep"
            desc="Generate anticipated questions, coaching notes, and direct-exam outlines."
          />
          <ToolCard
            title="Cross-Exam Generator"
            desc="Build aggressive cross-examination questions based on witness statements and prior testimony."
          />
          <ToolCard
            title="Deposition Prep"
            desc="Prepare deposition strategies with key questions and expected opposing responses."
          />
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'ai-tools-jury',
    title: 'Select & Understand Your Jury',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-paper-700 leading-relaxed">
          Build persona pools, predict juror questions, and craft instructions that resonate with your specific jury profile.
        </p>
        <div className="grid gap-2">
          <ToolCard
            title="Jury Pool"
            desc="Generate demographic profiles and personality traits for your virtual jury simulations."
          />
          <ToolCard
            title="Jury Selection"
            desc="Create targeted jury-selection questions to identify favorable and unfavorable jurors."
          />
          <ToolCard
            title="Juror Questions"
            desc="Predict the questions jurors will ask during deliberation and prepare answers."
          />
          <ToolCard
            title="Decision-Maker Instructions"
            desc="Draft plain-English decision-maker instructions that match your case theory and venue."
          />
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'ai-tools-presentation',
    title: 'Deliver a Winning Presentation',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-paper-700 leading-relaxed">
          From opening statement to closing argument, optimize every word for maximum impact on your jury.
        </p>
        <div className="grid gap-2">
          <ToolCard
            title="Opening Statement Optimizer"
            desc="Structure your opening to hook the jury, establish credibility, and frame the narrative."
          />
          <ToolCard
            title="Closing Argument Builder"
            desc="Weave together evidence, emotion, and law into a powerful closing that drives your decision."
          />
          <ToolCard
            title="Presentation Scorer"
            desc="Get AI feedback on clarity, persuasion, emotional impact, and legal precision."
          />
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'ai-tools-case',
    title: 'Manage Case Strategy & Settlement',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-paper-700 leading-relaxed">
          Beyond trial prep: strategic tools for negotiation, settlement, and case management.
        </p>
        <div className="grid gap-2">
          <ToolCard
            title="Case Strategy"
            desc="Map out your entire case strategy with timelines, milestones, and risk analysis."
          />
          <ToolCard
            title="Settlement Calculator"
            desc="Model settlement scenarios with probability-weighted outcomes and damage estimates."
          />
          <ToolCard
            title="Plea Bargain Analyzer"
            desc="Evaluate plea offers against trial outcomes to make data-driven decisions."
          />
          <ToolCard
            title="Mediation Prep"
            desc="Prepare mediation strategies with anticipated positions and counter-offers."
          />
          <ToolCard
            title="Settlement Demand Letter"
            desc="Generate persuasive demand letters with supporting damage calculations."
          />
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'mock-jury',
    title: 'Jury Simulations',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Run AI-powered jury simulations on any case. Test your arguments with virtual jurors, get decision predictions, and see exactly what resonates and what falls flat before you step into the courtroom.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider">Decision Distribution</span>
            <span className="text-xs text-paper-500">12 jurors</span>
          </div>
          <div className="flex items-end gap-2 h-24">
            <div className="flex-1 bg-navy-900" style={{ height: '60%' }} />
            <div className="flex-1 bg-paper-300" style={{ height: '40%' }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-paper-700">
            <span>Favorable outcome (60%)</span>
            <span>Unfavorable outcome (40%)</span>
          </div>
        </div>
        <p className="text-xs text-paper-500">
          Each simulation generates a full jury, delivers your argument, and produces a detailed debrief.
        </p>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'sample-case',
    title: 'Your Case Dashboard',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          All your cases live here. Each case holds your description, theory, argument, and any documents you upload. You can run as many simulations as you want on each case.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-navy-900">State v. Rodriguez</div>
              <div className="text-xs text-paper-500">Criminal Defense</div>
            </div>
            <span className="text-xs text-navy-900 border border-paper-300 px-2 py-1 font-medium">3 sims</span>
          </div>
          <div className="text-xs text-paper-700 leading-relaxed mb-3">
            Client pulled over for lane drifting. Breathalyzer read .09, but the officer had not calibrated the device in 14 months.
          </div>
          <div className="flex items-center gap-3 text-xs text-paper-500">
            <span>12 jurors</span>
            <span>Updated 2 days ago</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-paper-700">
          <span className="w-2 h-2 bg-navy-900 rounded-none" />
          Click any case to view details, run simulations, or export reports
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'simulation',
    title: 'Run a Jury Simulation',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Choose your jury size and venue. Your argument is already loaded. Hit "Run" and watch the results come in: decision distribution, juror reactions, and strategic recommendations.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">Simulation Setup</div>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-paper-500 mb-1">Jury Size</div>
              <div className="flex gap-2">
                <span className="px-3 py-1.5 border border-navy-900 bg-navy-900 text-white text-sm font-medium">12</span>
                <span className="px-3 py-1.5 border border-paper-300 bg-white text-paper-500 text-sm">8</span>
                <span className="px-3 py-1.5 border border-paper-300 bg-white text-paper-500 text-sm">6</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-paper-500 mb-1">Venue</div>
              <div className="px-3 py-2 border border-paper-300 bg-white text-sm text-paper-700">
                Urban working-class
              </div>
            </div>
            <div>
              <div className="text-xs text-paper-500 mb-1">Argument Preview</div>
              <div className="px-3 py-2 border border-paper-300 bg-paper-50 text-xs text-paper-700 leading-relaxed">
                The officer administered a breathalyzer that had not been calibrated in 14 months...
              </div>
            </div>
            <button className="w-full py-3 border border-navy-900 bg-navy-900 text-white text-sm font-medium" type="button">
              Run Simulation
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-paper-700">
          <span className="w-2 h-2 bg-navy-900 rounded-none" />
          Simulations run in seconds. Re-run with different arguments or panel sizes to test strategies.
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'results',
    title: 'Your Simulation Results',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Get detailed insights on every simulation: how jurors voted, what resonated, what fell flat, and what to change for your next run. Export a full report in one click.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">Simulation Results</div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
              <div className="text-2xl font-bold text-navy-900">60%</div>
              <div className="text-xs text-paper-500 mt-1">Favorable</div>
            </div>
            <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
              <div className="text-2xl font-bold text-navy-900">40%</div>
              <div className="text-xs text-paper-500 mt-1">Unfavorable</div>
            </div>
            <div className="bg-white border border-paper-300 p-3 text-center rounded-none">
              <div className="text-2xl font-bold text-navy-900">0.72</div>
              <div className="text-xs text-paper-500 mt-1">Resonance</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>Strongest:</strong> Breathalyzer calibration argument scored highest with analytical jurors</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-paper-300 rounded-none shrink-0" />
              <span><strong>Weakest:</strong> Traffic stop narrative lacked supporting video evidence</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>Swing Juror:</strong> Juror 7 - Pragmatic Engineer (persuaded by technical data)</span>
            </div>
          </div>
        </div>
        <div className="bg-white border border-paper-300 p-3 flex items-center gap-3 rounded-none">
          <span className="w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
          <div className="text-sm text-paper-700">
            Export a full PDF report with juror breakdowns, recommendations, and comparison charts
          </div>
        </div>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'contradictions-billing',
    title: 'Deeper Results & Billing',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Once you have uploaded two or more documents to a case, the <strong>Documents</strong> page can generate a contradiction report that finds conflicting statements across depositions, medical records, and other files.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">What's Included</div>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>Free first simulation:</strong> full headline verdict and debrief</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>After the first run:</strong> subscribe to unlock detailed contradiction reports and persona-by-persona reasoning</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>Plans:</strong> Solo (1-3 attorneys), Firm (4-10), or Growth (11-50), monthly or annual. Pay per case also available.</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-paper-700">
              <span className="mt-1 w-1.5 h-1.5 bg-navy-900 rounded-none shrink-0" />
              <span><strong>Free seats:</strong> Paralegal and Support Staff seats are free on every plan. They can upload documents and read results, but cannot open cases or run simulations</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-paper-500">
          Manage your plan, seats, and payment method from Firm Settings &gt; Billing.
        </p>
      </div>
    ),
    cta: 'Next',
    secondaryCta: 'Skip Tour',
  },
  {
    id: 'create-case',
    title: 'Create Your Own Case',
    content: (
      <div className="space-y-4">
        <p className="text-sm text-paper-700 leading-relaxed">
          Enter the basics now: name, practice area, and a brief description. You can add documents, arguments, and run simulations anytime.
        </p>
        <div className="bg-white border border-paper-300 p-4 rounded-none">
          <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">New Case</div>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-paper-500 mb-1">Case Name</div>
              <div className="px-3 py-2 border border-paper-300 bg-paper-50 text-sm text-paper-500">
                e.g., Smith v. ABC Corp
              </div>
            </div>
            <div>
              <div className="text-xs text-paper-500 mb-1">Practice Area</div>
              <div className="px-3 py-2 border border-paper-300 bg-paper-50 text-sm text-paper-500">
                Select practice area...
              </div>
            </div>
            <div>
              <div className="text-xs text-paper-500 mb-1">Description</div>
              <div className="px-3 py-2 border border-paper-300 bg-paper-50 text-sm text-paper-500 h-16">
                Brief description of the case...
              </div>
            </div>
            <div className="flex gap-2">
              <span className="px-2 py-1 border border-navy-900 bg-navy-900 text-white text-xs font-medium">Plaintiff</span>
              <span className="px-2 py-1 border border-paper-300 bg-white text-paper-500 text-xs font-medium">Defense</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-paper-700">
          <span className="w-2 h-2 bg-navy-900 rounded-none" />
          Once created, you can add arguments, upload documents, and run simulations immediately
        </div>
      </div>
    ),
    cta: 'Get Started',
    secondaryCta: 'Skip Tour',
  },
];
